import { useEffect } from "react";
import { motion } from "framer-motion";
import { HelpCircle, Send } from "lucide-react";
import { Link } from "wouter";
import { Chip } from "../components/ux/Chip";
import { Expandable } from "../components/ux/Expandable";
import Logo from "@/components/Logo";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { quizHref } from "../templates/shared";
import barClosedImg from "@/assets/images/chat_bar_closed.webp";
import thursdayImg from "@/assets/images/chat_thursday.webp";
import outcloseIcon from "@/assets/icons/outclose.svg";

/**
 * "The group chat" landing layout: a chat thread of plans that never happen,
 * Outclose interrupting as the plan that made itself, and the single CTA as
 * the sticky chat reply bar.
 *
 * Two ways to use it:
 * - The built-in sapphic 20-39 page (/v9gc, default export) resolves copy
 *   from i18n (pages.sapphicChat).
 * - As the "chat" template for MCP-created landing variants: ChatLayout takes
 *   a fully-resolved ChatConfig (plain strings). See docs/landing-templates.md.
 */

export interface ChatMsg {
  sender: string;
  text: string;
  /** "bar" | "thursday" for built-in photos, or an https image URL. */
  image?: string;
  imageAlt?: string;
}

export interface ChatConfig {
  posthogEvent: string;
  audienceParam: string;
  eventTypeParam?: string;
  chatName: string;
  chatStatus: string;
  cta: string;
  ctaPlaceholder: string;
  senders: Array<{ id: string; initial: string; colorClass: string }>;
  thread: Array<{ month: string; msgs: ChatMsg[] }>;
  typingSender: string;
  typingLabel: string;
  interrupt: { kicker: string; h2: string; sender: string; time: string; bodies: string[] };
  receiptsLabel: string;
  receipts: Array<{ quote: string; meta: string }>;
  photo?: { image: string; caption: string; alt: string };
  faqTitle: string;
  faq: Array<{ q: string; a: string }>;
}

/** Intrinsic sizes ship with the imports so photo bubbles reserve their space
 *  before decode. Without them every message below a photo jumps on load. A
 *  config-supplied URL has no known size, so it renders without them. */
const BUILTIN_IMAGES: Record<string, { src: string; width: number; height: number }> = {
  bar: { src: barClosedImg, width: 900, height: 675 },
  thursday: { src: thursdayImg, width: 900, height: 696 },
};

function imgProps(image: string) {
  const builtin = BUILTIN_IMAGES[image];
  return builtin ?? { src: image, width: undefined, height: undefined };
}

function Avatar({ initial, colorClass }: { initial: string; colorClass: string }) {
  return (
    <div
      className={`w-8 h-8 rounded-full ${colorClass} text-white text-sm font-semibold flex items-center justify-center shrink-0`}
    >
      {initial}
    </div>
  );
}

export function ChatLayout({ config }: { config: ChatConfig }) {
  useEffect(() => {
    tracking.trackPage(config.posthogEvent);
  }, [config.posthogEvent]);

  const { t: tRaw } = useT("landing");
  const senders = Object.fromEntries(config.senders.map((s) => [s.id, s]));
  const ctaHref = quizHref(config.audienceParam, config.eventTypeParam);

  const replyBar = (
    <a
      href={ctaHref}
      className="flex items-center gap-3 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-700 rounded-full pl-5 pr-2 py-2 shadow-lg group"
    >
      {/* min-w-0 so a longer translation truncates instead of wrapping the bar
          onto two lines. */}
      <span className="flex-1 min-w-0 truncate text-neutral-400 dark:text-neutral-500 text-[15px] select-none">
        {config.ctaPlaceholder}
      </span>
      <span className="shrink-0 rounded-full bg-primary-500 group-hover:bg-primary-600 text-white font-bold text-[15px] pl-5 pr-4 py-2.5 inline-flex items-center gap-2 transition-colors">
        {config.cta}
        <Send className="w-4 h-4" />
      </span>
    </a>
  );

  return (
    <div className="relative min-h-full bg-neutral-50 dark:bg-neutral-900 pb-28">
      {/* Chat header */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-neutral-900/90 backdrop-blur border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <div className="flex -space-x-2">
            {config.senders.map((s) => (
              <div key={s.id} className="ring-2 ring-white dark:ring-neutral-900 rounded-full">
                <Avatar initial={s.initial} colorClass={s.colorClass} />
              </div>
            ))}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-type-950 dark:text-neutral-100 truncate">
              {config.chatName}
            </p>
            <p className="text-xs text-neutral-400 truncate">{config.chatStatus}</p>
          </div>
          <Logo withIcon={true} className="scale-75 origin-right" />
        </div>
      </div>

      {/* The thread */}
      <div className="max-w-lg mx-auto px-4 pt-6 flex flex-col gap-2.5">
        {config.thread.map(({ month, msgs }, gi) => (
          <div key={gi} className="flex flex-col gap-2.5">
            <p className="text-center text-xs text-neutral-400 dark:text-neutral-600 font-medium mt-4 mb-1">
              {month}
            </p>
            {msgs.map((m, mi) => {
              const s = senders[m.sender] ?? config.senders[0];
              return (
                <motion.div
                  key={mi}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.35 }}
                  className="flex items-end gap-2"
                >
                  <Avatar initial={s.initial} colorClass={s.colorClass} />
                  <div className="max-w-[80%]">
                    {m.image && (
                      <img
                        {...imgProps(m.image)}
                        loading="lazy"
                        decoding="async"
                        alt={m.imageAlt ?? ""}
                        className="w-full h-auto rounded-2xl rounded-bl-md mb-1 shadow"
                      />
                    )}
                    <div className="bg-neutral-200 dark:bg-neutral-800 text-type-950 dark:text-neutral-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-[15px] leading-snug w-fit">
                      {m.text}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ))}

        {/* typing indicator that never resolves */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex items-center gap-2 mt-1"
        >
          <Avatar
            initial={(senders[config.typingSender] ?? config.senders[0]).initial}
            colorClass={(senders[config.typingSender] ?? config.senders[0]).colorClass}
          />
          <div className="bg-neutral-200 dark:bg-neutral-800 rounded-2xl rounded-bl-md px-4 py-3 w-fit flex gap-1.5">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className="w-2 h-2 rounded-full bg-neutral-400 dark:bg-neutral-500 animate-bounce"
                style={{ animationDelay: `${i * 150}ms`, animationDuration: "1.1s" }}
              />
            ))}
          </div>
          <p className="text-xs text-neutral-400 dark:text-neutral-600">{config.typingLabel}</p>
        </motion.div>
      </div>

      {/* The interrupt */}
      <div className="max-w-lg mx-auto px-4 mt-14 text-center">
        <p className="text-sm font-medium text-neutral-400 dark:text-neutral-500 uppercase tracking-widest mb-2">
          {config.interrupt.kicker}
        </p>
        <h1 className="text-3xl md:text-4xl font-semibold text-type-950 dark:text-neutral-50 font-Poppins mb-8">
          {config.interrupt.h2}
        </h1>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-left bg-white dark:bg-neutral-900 border border-primary-500/40 rounded-3xl shadow-xl shadow-primary-500/10 overflow-hidden"
        >
          <div className="flex items-center gap-3 px-5 pt-4">
            <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center">
              <img src={outcloseIcon} alt="" aria-hidden className="w-5 h-5 invert" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-type-950 dark:text-neutral-100">
                {config.interrupt.sender}
              </p>
            </div>
            <p className="text-xs text-neutral-400">{config.interrupt.time}</p>
          </div>
          <div className="px-5 pb-5 pt-3 flex flex-col gap-2">
            {config.interrupt.bodies.map((b, i) => (
              <div
                key={i}
                className="bg-primary-500 text-white rounded-2xl rounded-tl-md px-4 py-3 text-[15px] leading-snug"
              >
                {b}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Receipts */}
      <div className="max-w-lg mx-auto px-4 mt-16">
        <p className="text-center text-xs uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-6">
          {config.receiptsLabel}
        </p>
        <div className="flex flex-col gap-4">
          {config.receipts.map((r, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35 }}
              className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-5"
            >
              <p className="text-type-950 dark:text-neutral-200 text-[15px] leading-relaxed mb-2">
                “{r.quote}”
              </p>
              <p className="text-sm font-semibold text-neutral-400">{r.meta}</p>
            </motion.div>
          ))}

          {config.photo && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.35 }}
              className="flex items-end gap-2 mt-2"
            >
              <div className="w-8 h-8 rounded-full bg-primary-500 text-white text-sm font-semibold flex items-center justify-center shrink-0">
                ✦
              </div>
              <div className="max-w-[85%]">
                <img
                  {...imgProps(config.photo.image)}
                  loading="lazy"
                  decoding="async"
                  alt={config.photo.alt}
                  className="w-full h-auto rounded-2xl rounded-bl-md shadow mb-1"
                />
                <div className="bg-neutral-200 dark:bg-neutral-800 text-type-950 dark:text-neutral-100 rounded-2xl rounded-bl-md px-4 py-2.5 text-[15px] w-fit">
                  {config.photo.caption}
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-lg mx-auto px-4 mt-16">
        <div className="text-center mb-6">
          <Chip title={config.faqTitle} variant="primary" icon={<HelpCircle strokeWidth={2} />} />
        </div>
        <div className="flex flex-col gap-3">
          {config.faq.map((f, i) => (
            <Expandable key={i} title={f.q}>
              {f.a}
            </Expandable>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 mt-12 text-center text-xs text-neutral-400 dark:text-neutral-600">
        <p className="flex items-center justify-center gap-1.5 mb-2">
          <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gradient-to-br from-red-500 via-yellow-400 to-violet-500" />
          {tRaw("footer.proudlyLgbtqOwned")}
        </p>
        <p>
          <Link href="/terms" className="underline hover:text-primary-500 transition-colors">
            {tRaw("footer.terms")}
          </Link>
          {" · "}
          <Link href="/privacy" className="underline hover:text-primary-500 transition-colors">
            {tRaw("footer.privacy")}
          </Link>
        </p>
      </div>

      {/* Sticky reply bar — THE one CTA, always reachable */}
      <div className="fixed bottom-0 inset-x-0 z-50 bg-gradient-to-t from-neutral-50 dark:from-neutral-900 via-neutral-50/95 dark:via-neutral-900/95 to-transparent pt-6 pb-4 px-4">
        <div className="max-w-lg mx-auto">{replyBar}</div>
      </div>
    </div>
  );
}

/** Built-in sapphic 20-39 page (/v9gc): copy resolved from i18n. */
export default function LandingSapphicChat() {
  const { t: tRaw } = useT("landing");
  const t = (key: string) => tRaw(`pages.sapphicChat.${key}`);

  const config: ChatConfig = {
    posthogEvent: "sapphic_chat_landing",
    audienceParam: "s",
    chatName: t("chatName"),
    chatStatus: t("chatStatus"),
    cta: t("cta"),
    ctaPlaceholder: t("ctaPlaceholder"),
    senders: [
      { id: "mia", initial: "M", colorClass: "bg-rose-400" },
      { id: "riley", initial: "R", colorClass: "bg-violet-400" },
      { id: "jaz", initial: "J", colorClass: "bg-amber-400" },
    ],
    thread: [
      { month: t("months.jan"), msgs: [
        { sender: "mia", text: t("msgs.jan1") },
        { sender: "riley", text: t("msgs.jan2") },
        { sender: "jaz", text: t("msgs.jan3") },
        { sender: "mia", text: t("msgs.jan4") },
      ]},
      { month: t("months.feb"), msgs: [
        { sender: "riley", text: t("msgs.feb1") },
        { sender: "jaz", text: t("msgs.feb2") },
      ]},
      { month: t("months.mar"), msgs: [
        { sender: "mia", text: t("msgs.mar1") },
        { sender: "riley", text: t("msgs.mar2") },
        { sender: "jaz", text: t("msgs.mar3") },
      ]},
      { month: t("months.may"), msgs: [{ sender: "riley", text: t("msgs.may1") }] },
      { month: t("months.jul"), msgs: [
        { sender: "mia", text: t("msgs.jul1") },
        { sender: "jaz", text: t("msgs.jul2"), image: "bar", imageAlt: t("receipts.barPhotoAlt") },
        { sender: "riley", text: t("msgs.jul3") },
      ]},
      { month: t("months.aug"), msgs: [
        { sender: "riley", text: t("msgs.aug1") },
        { sender: "mia", text: t("msgs.aug2") },
      ]},
    ],
    typingSender: "jaz",
    typingLabel: t("msgs.typing"),
    interrupt: {
      kicker: t("interrupt.kicker"),
      h2: t("interrupt.h2"),
      sender: t("interrupt.sender"),
      time: t("interrupt.time"),
      bodies: [t("interrupt.body1"), t("interrupt.body2")],
    },
    receiptsLabel: t("receipts.label"),
    receipts: [
      { quote: t("receipts.jess"), meta: t("receipts.jessMeta") },
      { quote: t("receipts.sam"), meta: t("receipts.samMeta") },
      { quote: t("receipts.maya"), meta: t("receipts.mayaMeta") },
    ],
    photo: { image: "thursday", caption: t("receipts.photoCaption"), alt: t("receipts.photoAlt") },
    faqTitle: t("faq.chipTitle"),
    faq: (["dating", "shy", "who", "cost", "click"] as const).map((k) => ({
      q: t(`faq.${k}.q`),
      a: t(`faq.${k}.a`),
    })),
  };

  return <ChatLayout config={config} />;
}
